/**
 * Telegram Mini App 冷启动鉴权交换（ADR-0004 §4.2）。
 *
 * 调用链：
 *   App mount
 *     → readTelegramContext()  // lib/telegram.ts，同步读 window.Telegram.WebApp
 *     → exchangeInitDataForSession(initDataRaw)
 *     → POST /api/v1/consumer/auth/telegram  { initData }
 *     → 存 memory store；后续 apiFetch 自动带 Bearer
 *
 * 在非 Telegram 环境（`vite dev` 浏览器打开、E2E 模拟器）：
 *   - initDataRaw 为空 → 标 'error' + code='DEV_NO_INITDATA'，由 UI 层展示"请在 Telegram 中打开"
 *   - 不做 localStorage 回填（ADR-0004 §4.2 #3）
 */

import { apiFetch, type ApiError } from './api';
import { setAuthState, type AuthUser } from './auth-store';

interface ConsumerAuthResponse {
  token: string;
  user: AuthUser;
}

export class MiniAppAuthError extends Error {
  code: string;
  status: number;
  traceId?: string;
  constructor(code: string, message: string, status = 0, traceId?: string) {
    super(message);
    this.name = 'MiniAppAuthError';
    this.code = code;
    this.status = status;
    this.traceId = traceId;
  }
}

export async function exchangeInitDataForSession(
  initDataRaw: string,
): Promise<ConsumerAuthResponse> {
  if (!initDataRaw) {
    setAuthState({
      status: 'error',
      errorCode: 'DEV_NO_INITDATA',
      errorMessage: 'Telegram initData unavailable. Open this page inside a Telegram client.',
    });
    throw new MiniAppAuthError(
      'DEV_NO_INITDATA',
      'Telegram initData unavailable (running outside a Telegram client)',
    );
  }

  setAuthState({ status: 'authenticating', errorCode: null, errorMessage: null });

  try {
    const data = await apiFetch<ConsumerAuthResponse>('/api/v1/consumer/auth/telegram', {
      method: 'POST',
      body: { initData: initDataRaw },
    });
    setAuthState({
      status: 'authenticated',
      token: data.token,
      user: data.user,
      errorCode: null,
      errorMessage: null,
    });
    return data;
  } catch (err) {
    const apiErr = err as ApiError;
    setAuthState({
      status: 'error',
      errorCode: apiErr.code ?? 'UNKNOWN_ERROR',
      errorMessage: apiErr.message,
      traceId: apiErr.traceId ?? null,
    });
    throw new MiniAppAuthError(
      apiErr.code ?? 'UNKNOWN_ERROR',
      apiErr.message,
      apiErr.status,
      apiErr.traceId,
    );
  }
}
